import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useNotesStore, useUIStore, useAuthStore } from '../store';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import {
  ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuTrigger
} from './ui/context-menu';
import { FileText, Plus, Settings, LogOut, ChevronRight, ChevronDown, FolderOpen, Search, Hash, Star, Clock, Trash2, BookOpen, LayoutGrid, Download, Upload } from 'lucide-react';

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { notes, tags, folders, fetchNotes, fetchTags, fetchFolders, createNote, deleteNote, toggleStar } = useNotesStore();
  const { addToast } = useUIStore();
  const { user, logout } = useAuthStore();
  const [expandedFolders, setExpandedFolders] = useState({});
  const [showNewNote, setShowNewNote] = useState(false);
  const [newNoteTitle, setNewNoteTitle] = useState('');

  useEffect(() => { fetchNotes(); fetchTags(); fetchFolders(); }, []);

  const handleCreateNote = async (e) => {
    e.preventDefault();
    if (!newNoteTitle.trim()) return;
    const note = await createNote({ title: newNoteTitle, content: `# ${newNoteTitle}\n\n`, folder: '/' });
    setNewNoteTitle(''); setShowNewNote(false);
    navigate(`/note/${note.id}`);
    addToast({ title: 'Note created', type: 'success' });
  };

  const handleExport = async () => {
    try {
      const api = (await import('../api')).default;
      const data = await api.exportNotes();
      const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `knowledge-export-${Date.now()}.json`; a.click();
      addToast({ title: 'Exported successfully', type: 'success' });
    } catch { addToast({ title: 'Export failed', type: 'error' }); }
  };

  const toggleFolder = (f) => setExpandedFolders(p => ({ ...p, [f]: !p[f] }));

  const notesByFolder = notes.reduce((acc, note) => {
    const f = note.folder || '/';
    if (!acc[f]) acc[f] = [];
    acc[f].push(note);
    return acc;
  }, {});

  const navItems = [
    { path: '/?starred=true', icon: Star, label: 'Starred' },
    { path: '/recent', icon: Clock, label: 'Recent' },
    { path: '/templates', icon: BookOpen, label: 'Templates' },
    { path: '/graph', icon: LayoutGrid, label: 'Graph View' },
    { path: '/trash', icon: Trash2, label: 'Trash' },
  ];

  const NoteItem = ({ note }) => (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <Link to={`/note/${note.id}`} className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${location.pathname === `/note/${note.id}` ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}>
          <FileText size={14} className="flex-shrink-0" />
          <span className="truncate flex-1">{note.title}</span>
          {note.starred ? <Star size={12} className="text-yellow-500 fill-yellow-500" /> : null}
        </Link>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-48">
        <ContextMenuItem onClick={() => navigate(`/note/${note.id}`)}>Open</ContextMenuItem>
        <ContextMenuItem onClick={() => toggleStar(note.id)}>{note.starred ? 'Unstar' : 'Star'}</ContextMenuItem>
        <ContextMenuItem onClick={() => navigator.clipboard.writeText(note.title)}>Copy Title</ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem className="text-destructive" onClick={() => { if (confirm('Delete?')) deleteNote(note.id); }}>Delete</ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );

  return (
    <div className="h-full bg-card border-r flex flex-col">
      <div className="p-4 border-b">
        <h1 className="text-lg font-semibold flex items-center gap-2"><span className="text-primary">⚡</span> Knowledge</h1>
      </div>

      <div className="p-3">
        <Button onClick={() => setShowNewNote(!showNewNote)} className="w-full justify-start gap-2" variant="outline">
          <Plus size={16} /> New Note
        </Button>
        {showNewNote && (
          <form onSubmit={handleCreateNote} className="mt-2">
            <Input type="text" value={newNoteTitle} onChange={e => setNewNoteTitle(e.target.value)} placeholder="Note title..." className="text-sm" autoFocus />
          </form>
        )}
      </div>

      <div className="px-3 mb-2">
        <button onClick={() => navigate('/?search=true')} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground bg-muted rounded-md hover:bg-muted/80 transition-colors">
          <Search size={14} /> Search notes...
        </button>
      </div>

      <ScrollArea className="flex-1 px-3">
        {/* Quick nav */}
        <div className="mb-4">
          {navItems.map(item => (
            <Link key={item.path} to={item.path} className={`flex items-center gap-2 px-2 py-1.5 text-sm rounded-md transition-colors ${location.pathname === item.path ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-muted'}`}>
              <item.icon size={14} /> {item.label}
            </Link>
          ))}
        </div>

        {/* Folders */}
        <div className="mb-4">
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 px-2">Folders</h3>
          {Object.entries(notesByFolder).map(([folder, folderNotes]) => (
            <ContextMenu key={folder}>
              <ContextMenuTrigger asChild>
                <div>
                  <button onClick={() => toggleFolder(folder)} className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-muted transition-colors">
                    {expandedFolders[folder] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <FolderOpen size={14} className="text-muted-foreground" />
                    <span className="truncate">{folder === '/' ? 'Root' : folder}</span>
                    <span className="ml-auto text-xs text-muted-foreground">{folderNotes.length}</span>
                  </button>
                  {expandedFolders[folder] && (
                    <div className="ml-4 mt-0.5 space-y-0.5">
                      {folderNotes.map(n => <NoteItem key={n.id} note={n} />)}
                    </div>
                  )}
                </div>
              </ContextMenuTrigger>
              <ContextMenuContent className="w-48">
                <ContextMenuItem onClick={() => {}}>New Note Here</ContextMenuItem>
              </ContextMenuContent>
            </ContextMenu>
          ))}
        </div>

        {/* Tags */}
        <div className="mb-4">
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 px-2">Tags</h3>
          {tags.map(tag => (
            <ContextMenu key={tag}>
              <ContextMenuTrigger asChild>
                <button onClick={() => navigate(`/?tag=${encodeURIComponent(tag)}`)} className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-muted-foreground rounded-md hover:bg-muted transition-colors">
                  <Hash size={14} /> <span className="truncate">{tag}</span>
                </button>
              </ContextMenuTrigger>
              <ContextMenuContent className="w-48">
                <ContextMenuItem onClick={() => navigator.clipboard.writeText(tag)}>Copy Tag</ContextMenuItem>
              </ContextMenuContent>
            </ContextMenu>
          ))}
        </div>
      </ScrollArea>

      <div className="p-3 border-t space-y-2">
        <Button variant="outline" size="sm" className="w-full justify-start gap-2" onClick={handleExport}>
          <Download size={14} /> Export
        </Button>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-xs font-medium text-primary">{user?.username?.charAt(0).toUpperCase()}</span>
            </div>
            <span className="text-sm text-muted-foreground">{user?.username}</span>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate('/settings')}><Settings size={14} /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => { logout(); navigate('/login'); }}><LogOut size={14} /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}
